/**
 * woo coworking design tokens. Single source for Tailwind (web), NativeWind (mobile),
 * react-native-svg / SVG fills and chart colours. Do not hard-code hex values anywhere else.
 */
export const color = {
  // Brand
  teal: '#37839d', //        Woo-Teal: primary buttons, links, brand, "mine" desk fill
  tealDark: '#2b6a80', //    hover/pressed of teal, icons, secondary emphasised text, floor-plan strokes
  lime: '#d9e900', //        Woo-Lime: ONE highlight per screen (success, selected dot, hero CTA)
  ink: '#151515', //         headings, primary text on light, dark footer background
  // Text
  text: '#3a3a3a', //        body text ("text-2" in the artboards)
  muted: '#6b7478', //       secondary text, inactive tab
  placeholder: '#9aa4a8', // input placeholder, disabled text, taken-desk label
  onTeal: '#ffffff',
  onLime: '#151515',
  limeInk: '#747d00', //     lime-coloured text on white (badges, target line in charts)
  // Lines and surfaces
  line: '#dfe6e8', //        default 1 px border
  lineStrong: '#c9d1d4', //  input border (resting), dashed empty cards
  hairline: '#eef2f3', //    row separators inside cards
  white: '#ffffff',
  fog: '#f4f7f8', //         "Nebel": page sections, icon-button background, segmented track
  tealTint: '#e7f0f3', //    "Teal hell": chips, selection, reserved-desk fill
  tealTintSoft: '#f3f8f9', //selected row/card background, filled OTP box
  disabled: '#e3e8ea', //    disabled button bg, taken-desk fill
  limeTint: '#fbfcdd', //    upsell box background
  limeTintLine: '#e8ee8a', //upsell box border
  // Status
  warnBg: '#fff3cc',
  warnText: '#8a6100',
  warnDot: '#e0a100', //     "scarce" day dot, maintenance desk
  successBg: '#e9f7d9',
  successText: '#3d6b12',
  dangerBg: '#fde2e2',
  dangerText: '#9b2c2c',
  // Scanner (dark camera screen)
  scanTop: '#3a3f41',
  scanMid: '#1c1f20',
  scanBottom: '#151515',
  // Analytics series (canvas note "Farben"): FlexDesk teal, PrivateDesk teal dark,
  // Office teal light, Tagesticket coral, Add-ons sand, previous month grey, target = limeInk
  chartFlexDesk: '#37839d',
  chartPrivateDesk: '#2b6a80',
  chartOffice: '#8fc1d1',
  chartDayTicket: '#e8735a', // "Koralle"; also campaign-day markers
  chartAddons: '#e0c37a', //    "Sand"
  chartPrevious: '#e9eef0', //  previous month (grey)
  chartTarget: '#747d00', //    target line ("Limette-Tinte")
  // Plan/forecast values: the series colour at opacity 0.45 with a dashed 1.5 px stroke "4 3"
  // Heat ramps (popular desks, peak hours): tealTint -> #a8c3cc -> teal -> tealDark
  chartHeat: ['#e7f0f3', '#a8c3cc', '#37839d', '#2b6a80'],
} as const;

export const font = {
  heading: 'Syne', //  700 headings, prices, numbers on success screens; 800 wordmark only
  body: 'Figtree', //  everything that is read, 400/500/600/700
  fallbackWeb: "'Avenir Next', 'Helvetica Neue', Arial, sans-serif",
} as const;

/** [fontSize px, lineHeight px, fontWeight, fontFamily, letterSpacing em] */
export const type = {
  // Mobile app
  display: [52, 56, 700, 'heading', -0.02], //  success screens ("Platz 14")
  h1: [28, 34, 700, 'heading', -0.01], //       screen headline (artboards use 26–30)
  h2: [22, 28, 700, 'heading', -0.01], //       hero card value, sheet titles
  title: [18, 24, 700, 'heading', -0.01], //    stack header title, card titles
  price: [20, 24, 700, 'heading', 0], //        amounts, always tabular-nums
  bodyLg: [16, 24, 400, 'body', 0],
  body: [15, 22, 400, 'body', 0],
  bodySm: [13, 18, 400, 'body', 0],
  button: [16, 20, 600, 'body', 0],
  label: [11, 14, 600, 'body', 0.08], //        uppercase field/section label (".lab")
  tab: [11, 14, 500, 'body', 0], //             tab bar label; active = 700
  // Web (site, member, admin)
  webDisplay: [64, 68, 700, 'heading', -0.02], // landing hero H1
  webH1: [44, 50, 700, 'heading', -0.02],
  webH2: [36, 42, 700, 'heading', -0.01],
  webH3: [20, 28, 700, 'heading', -0.01],
  webBody: [18, 28, 400, 'body', 0], //         "Fließtext in 18 px, Zeilenabstand 1,55"
  webSmall: [15, 22, 400, 'body', 0],
  webEyebrow: [13, 18, 600, 'body', 0.08], //   uppercase
  webButton: [16, 20, 600, 'body', 0],
  adminBody: [14, 20, 400, 'body', 0], //       dense tables in admin
  adminSmall: [12, 16, 400, 'body', 0],
} as const;

export const radius = {
  segmentInner: 9,
  buttonSm: 10, //  36 px action buttons (".abtn")
  input: 12, //     fields, 40 px icon tiles, 44 px back button
  button: 14,
  card: 16, //      default card; 18 allowed for large cards
  tierCard: 20, //  membership tier cards, web cards (20–28)
  hero: 22, //      Home hero card
  webCard: 24,
  pill: 999,
} as const;

export const size = {
  touchTarget: 44, //   minimum hit area, both platforms
  buttonHeight: 52, //  primary/secondary/lime/danger (54 allowed in sticky footers)
  buttonHeightWeb: 52, // web range 48–56
  buttonSmHeight: 36,
  fieldHeight: 48,
  segmentedHeight: 40, // 36 in dense headers
  iconTile: 40, //      ".ic"
  headerHeight: 56, //  stack header
  statusInset: 54, //   design status-bar inset; use safe-area insets in code
  tabBarHeight: 84, //  incl. bottom safe area on iPhone
  deskMobile: 44, //    rendered desk square on the mobile plan at zoom 1
  otpBox: [48, 56], //  width, height
} as const;

/** 4-pt scale. Screen gutter 20 (mobile), card padding 16, gap between cards 12. */
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

export const shadow = {
  none: 'none',
  sm: '0 1px 3px rgba(21,21,21,0.12)', //      cards on fog, segmented thumb
  md: '0 8px 24px rgba(21,21,21,0.12)', //     bottom sheet, sticky footer, popovers
  lg: '0 20px 50px rgba(21,21,21,0.18)', //    modals (web)
  xl: '0 30px 80px rgba(21,21,21,0.30)', //    admin sticker modal
  limeGlow: '0 0 16px #d9e900', //             scan line
} as const;

/** React Native has no box-shadow string on Android < 28: use these instead. */
export const elevation = { sm: 1, md: 6, lg: 12 } as const;

export const motion = {
  // cowooPulse: three rings behind the Access circle, staggered 0 / 0.8 / 1.6 s
  pulse: {
    durationMs: 2400,
    easing: 'ease-out',
    iteration: 'infinite',
    delaysMs: [0, 800, 1600],
    from: { scale: 0.72, opacity: 0.9 },
    to: { scale: 1.35, opacity: 0 },
  },
  // cowooScan: lime scan line inside the QR frame, top 10 % -> 88 %, alternating
  scan: {
    durationMs: 2200,
    easing: 'ease-in-out',
    iteration: 'infinite',
    direction: 'alternate',
    fromTopPct: 10,
    toTopPct: 88,
  },
  press: { durationMs: 120 }, //  opacity 1 -> 0.85 on press
  sheet: { durationMs: 260 }, //  bottom sheet slide
  reduceMotion:
    'When the OS "reduce motion" setting is on: no pulse rings (static ring), no scan line movement, sheets fade instead of slide.',
} as const;

export const breakpoint = { sm: 640, md: 768, lg: 1024, xl: 1280, content: 1200 } as const;

/** Floor-plan desk states -> exact colours (see § 4.12). */
export const deskState = {
  free: { fill: '#ffffff', stroke: '#37839d', strokeWidth: 1.5, dash: null, label: '#2b6a80' },
  taken: { fill: '#e3e8ea', stroke: '#e3e8ea', strokeWidth: 1.5, dash: null, label: '#9aa4a8' },
  selected: { fill: '#37839d', stroke: '#37839d', strokeWidth: 1.5, dash: null, label: '#ffffff' },
  mine: { fill: '#37839d', stroke: '#d9e900', strokeWidth: 3, dash: null, label: '#ffffff' },
  reserved: { fill: '#e7f0f3', stroke: '#37839d', strokeWidth: 1.5, dash: '4 3', label: '#2b6a80' },
  occupied: { fill: '#37839d', stroke: '#37839d', strokeWidth: 1.5, dash: null, label: '#ffffff' },
  blocked: { fill: '#e3e8ea', stroke: '#c9d1d4', strokeWidth: 1.5, dash: null, label: '#9aa4a8' },
  maintenance: { fill: '#fff3cc', stroke: '#e0a100', strokeWidth: 1.5, dash: null, label: '#8a6100' }, // admin only
  fixed: { fill: '#f4f7f8', stroke: '#c9d1d4', strokeWidth: 1.5, dash: null, label: '#9aa4a8' }, // PrivateDesks in pickers
} as const;

export type ColorToken = keyof typeof color;
export type DeskState = keyof typeof deskState;
